import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './styles/header-modular.css';
import logo2 from './image/logo.png';

// Función throttle para optimizar el scroll
const throttle = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  let lastExecTime = 0;
  return function (this: any, ...args: any[]) {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

// Fuente única de verdad para las rutas y etiquetas
const NAV_ITEMS = [
    { to: '/', label: 'Inicio' },
    { to: '/Us', label: 'Quienes Somos' },
    { to: '/pricing', label: 'Programas y Servicios' },
    { to: '/contact', label: 'Contacto' },
];

const Header: React.FC = React.memo(() => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleScroll = useCallback(() => {
        const throttledScroll = throttle(() => {
            setScrolled(window.scrollY > 0);
        }, 100);
        throttledScroll();
    }, []);

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prev => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setMobileMenuOpen(false);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Cerrar menú móvil al cambiar de ruta
    useEffect(() => {
        const handleRouteChange = () => {
            setMobileMenuOpen(false);
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, []);

    // Prevenir scroll del body cuando el menú móvil está abierto
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    return (
        <>
            <header className={`sticky-navbar${scrolled ? ' scrolled' : ''}`}>
                <div className="header__container">
                    <Link to="/" className="header__brand">
                        <img src={logo2} alt="CECAP" className="header__logo" />
                        <h1 className="header__logo2">CECAP</h1>
                    </Link>
                    
                    {/* Navegación Desktop */}
                    <nav className="header__nav">
                        {NAV_ITEMS.map(item => (
                            <Link key={item.to} to={item.to} className="header__link">{item.label}</Link>
                        ))}
                    </nav>

                    {/* Acciones Desktop */}
                    <div className="header__actions">
                        <Link to="/portales" className="header__link header__portal-link">
                            Portales
                        </Link>
                    </div>

                    {/* Botón Hamburguesa */}
                    <button 
                        className={`hamburger-menu${mobileMenuOpen ? ' active' : ''}`}
                        onClick={toggleMobileMenu}
                        aria-label="Toggle mobile menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
            </header>

            {/* Menú Móvil */}
            <div className={`mobile-menu${mobileMenuOpen ? ' active' : ''}`}>
                <div className="mobile-menu-header">
                    <img src={logo2} alt="CECAP" className="mobile-logo" />
                    <h2 className="mobile-title">CECAP</h2>
                    <button 
                        className="mobile-menu-close"
                        onClick={closeMobileMenu}
                        aria-label="Close mobile menu"
                    >
                        <span></span>
                        <span></span>
                    </button>
                </div>
                
                <nav className="mobile-nav">
                    {NAV_ITEMS.map(item => (
                        <Link key={item.to} to={item.to} className="mobile-link" onClick={closeMobileMenu}>
                            <span>{item.label}</span>
                        </Link>
                    ))}

                    <Link to="/portales" className="mobile-link mobile-link--portal" onClick={closeMobileMenu}>
                        <span>Portales</span>
                    </Link>
                </nav>

                
            </div>

            {/* Overlay para cerrar menú */}
            {mobileMenuOpen && (
                <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
            )}
        </>
    );
});

export default Header;
